import axios from 'axios';
import DOMPurify from 'dompurify';
import dbUrl from '../utils/credentials';
import { formatDateTimeDisplay, parseLocalDateTime, toDatetimeLocalValue as toDatetimeLocalValueFromLib } from './dateTime';

export const DEFAULT_THEME = {
  layout: 'classic',
  buttonText: 'Partecipa',
  buttonStyle: 'rounded',
  coverStyle: 'full',
  fontScale: 'normal',
  fontFamily: 'ubuntu',
  backgroundImage: '',
};

export const DEFAULT_LOCATION = {
  address: '',
  lat: null,
  lng: null,
  markerUrl: '',
  showMap: true,
  showDirections: true,
  markers: [],
};

export const DEFAULT_SUCCESS_MESSAGE = 'Richiesta inviata! Grazie';
export const DEFAULT_CLOSED_MESSAGE = 'Le iscrizioni per questo invito sono chiuse.';

export function createLocationMarker(partial = {}) {
  const lat = partial.lat == null || partial.lat === '' ? null : Number(partial.lat);
  const lng = partial.lng == null || partial.lng === '' ? null : Number(partial.lng);
  return {
    id: partial.id || `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    name: (partial.name || '').toString(),
    markerUrl: partial.markerUrl || '',
  };
}

function normalizeLocationMarkers(raw, fallbackLat, fallbackLng, fallbackMarkerUrl) {
  const hasMarkersKey = raw && Object.prototype.hasOwnProperty.call(raw, 'markers');

  if (hasMarkersKey && Array.isArray(raw.markers)) {
    return raw.markers
      .map((item) => createLocationMarker(item))
      .filter((m) => m.lat != null && m.lng != null);
  }

  if (fallbackLat != null && fallbackLng != null) {
    return [
      createLocationMarker({
        lat: fallbackLat,
        lng: fallbackLng,
        markerUrl: fallbackMarkerUrl || '',
      }),
    ];
  }

  return [];
}

export function normalizeLocation(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_LOCATION, markers: [] };
  }
  const lat = raw.lat == null || raw.lat === '' ? null : Number(raw.lat);
  const lng = raw.lng == null || raw.lng === '' ? null : Number(raw.lng);
  const safeLat = Number.isFinite(lat) ? lat : null;
  const safeLng = Number.isFinite(lng) ? lng : null;
  const markerUrl = raw.markerUrl || '';
  const markers = normalizeLocationMarkers(raw, safeLat, safeLng, markerUrl);
  const primary = markers[0] || null;

  return {
    address: (raw.address || '').toString(),
    lat: primary ? primary.lat : null,
    lng: primary ? primary.lng : null,
    markerUrl,
    showMap: raw.showMap !== false,
    showDirections: raw.showDirections !== false,
    markers,
  };
}

export function hasInvitationLocation(location) {
  const loc = normalizeLocation(location);
  return Boolean(
    loc.address?.trim()
    || (loc.lat != null && loc.lng != null)
    || loc.markers.length > 0
  );
}

export function locationHasMapMarkers(location) {
  const loc = normalizeLocation(location);
  return loc.showMap !== false && loc.markers.length > 0;
}

export const INVITATION_LIGHT_THEME_VARS = {
  '--ba-body-bg': '#f4f0e8',
  '--ba-body-text': '#1f2937',
  '--ba-surface-bg': '#fffdf9',
  '--ba-surface-border': 'rgba(57, 94, 20, 0.14)',
  '--ba-input-bg': '#ffffff',
  '--ba-input-border': '#e5d5c0',
  '--ba-input-text': '#212529',
  '--ba-muted': '#5f6b7a',
  '--ba-accent-orange-subtle': 'rgba(246, 139, 31, 0.12)',
  '--ba-accent-green-subtle': 'rgba(57, 94, 20, 0.1)',
  '--ba-hover-subtle': 'rgba(246, 139, 31, 0.08)',
  '--ba-card-shadow': '0 1px 2px rgba(246, 139, 31, 0.06), 0 4px 14px rgba(57, 94, 20, 0.05)',
};

export const FIELD_TYPES = [
  { value: 'text', label: 'Testo' },
  { value: 'textarea', label: 'Testo lungo' },
  { value: 'number', label: 'Numero' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Telefono' },
  { value: 'date', label: 'Data' },
  { value: 'checkbox', label: 'Consenso / Checkbox' },
  { value: 'select', label: 'Scelta opzione/i' },
];

export const THEME_LAYOUTS_META = [
  {
    value: 'classic',
    label: 'Classico',
    description: 'Centrato, copertina e form strutturato',
  },
  {
    value: 'card',
    label: 'Card',
    description: 'Contenuto in una card con bordo evidenziato',
  },
  {
    value: 'minimal',
    label: 'Minimal',
    description: 'Allineato a sinistra, layout più essenziale',
  },
];

export const CONDITION_OPERATORS = [
  { value: 'equals', label: 'è uguale a' },
  { value: 'not_equals', label: 'non è uguale a' },
  { value: 'contains', label: 'contiene' },
  { value: 'is_checked', label: 'è selezionato' },
  { value: 'is_filled', label: 'è compilato' },
];

export const OPERATORS_WITHOUT_VALUE = ['is_checked', 'is_filled'];

export function generateFieldId() {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function normalizeShowWhen(showWhen) {
  if (!showWhen || !showWhen.field) return null;
  return {
    field: showWhen.field,
    operator: showWhen.operator || 'equals',
    value: showWhen.value ?? '',
  };
}

export const INVITATION_TEMPLATES = [
  {
    id: 'raccolta',
    name: 'Raccolta alimentare',
    description: 'Template per raccolte e donazioni.',
    data: {
      name: 'Raccolta alimentare',
      description: 'Compila il modulo per partecipare alla raccolta alimentare.',
      color1: '#F68B1F',
      color2: '#395E14',
      fields: [
        { name: 'Nome e cognome', type: 'text', required: true, options: [], multiple: false },
        { name: 'Email', type: 'email', required: true, options: [], multiple: false },
        { name: 'Telefono', type: 'tel', required: false, options: [], multiple: false },
        { name: 'Turno', type: 'select', required: true, multiple: false, options: [{ label: 'Mattina', maxCount: null }, { label: 'Pomeriggio', maxCount: null }] },
        { name: 'Accetto il trattamento dei dati', type: 'checkbox', required: true, options: [], multiple: false },
      ],
    },
  },
  {
    id: 'volontari',
    name: 'Volontari',
    description: 'Iscrizione volontari per eventi.',
    data: {
      name: 'Volontari evento',
      description: 'Registrati come volontario per l\'evento.',
      color1: '#2563eb',
      color2: '#1e3a5f',
      fields: [
        { name: 'Nome e cognome', type: 'text', required: true, options: [], multiple: false },
        { name: 'Email', type: 'email', required: true, options: [], multiple: false },
        { name: 'Disponibilità', type: 'select', required: true, multiple: true, options: [{ label: 'Lunedì', maxCount: null }, { label: 'Martedì', maxCount: null }, { label: 'Mercoledì', maxCount: null }] },
      ],
    },
  },
  {
    id: 'evento',
    name: 'Evento benefico',
    description: 'Invito generico per eventi.',
    data: {
      name: 'Evento benefico',
      description: 'Conferma la tua partecipazione all\'evento.',
      color1: '#7c3aed',
      color2: '#4c1d95',
      fields: [
        { name: 'Nome e cognome', type: 'text', required: true, options: [], multiple: false },
        { name: 'Numero partecipanti', type: 'number', required: true, options: [], multiple: false },
        { name: 'Note', type: 'text', required: false, options: [], multiple: false },
      ],
    },
  },
];

export const THEME_LAYOUTS = [
  { value: 'classic', label: 'Classico' },
  { value: 'card', label: 'Card' },
  { value: 'minimal', label: 'Minimal' },
];

export const BUTTON_STYLES = [
  { value: 'rounded', label: 'Arrotondato' },
  { value: 'pill', label: 'Pill' },
  { value: 'square', label: 'Quadrato' },
];

export const COVER_STYLES = [
  { value: 'full', label: 'Larga' },
  { value: 'contained', label: 'Contenuta' },
  { value: 'none', label: 'Nascosta' },
];

export const FONT_SCALES = [
  { value: 'compact', label: 'Compatta' },
  { value: 'normal', label: 'Normale' },
  { value: 'large', label: 'Grande' },
];

export const FONT_FAMILIES = [
  { value: 'ubuntu', label: 'Ubuntu', cssFamily: "'Ubuntu', system-ui, sans-serif" },
  { value: 'poppins', label: 'Poppins', cssFamily: "'Poppins', system-ui, sans-serif" },
];

export function resolveInvitationFontFamily(fontFamily) {
  const match = FONT_FAMILIES.find((f) => f.value === fontFamily);
  return (match || FONT_FAMILIES[0]).cssFamily;
}

export const COLOR_PRESETS = [
  { name: 'Banco Alimentare', color1: '#F68B1F', color2: '#395E14' },
  { name: 'Blu elegante', color1: '#2563eb', color2: '#1e3a5f' },
  { name: 'Viola evento', color1: '#7c3aed', color2: '#4c1d95' },
  { name: 'Rosso festa', color1: '#dc2626', color2: '#991b1b' },
  { name: 'Verde natura', color1: '#16a34a', color2: '#14532d' },
];

export function normalizeOption(opt) {
  if (typeof opt === 'string') return { label: opt, maxCount: null, hidden: false };
  return {
    label: opt?.label || '',
    maxCount: opt?.maxCount ?? null,
    hidden: !!opt?.hidden,
  };
}

export function getOptionLabel(opt) {
  return typeof opt === 'string' ? opt : opt?.label || '';
}

export function isOptionVisible(opt) {
  return !normalizeOption(opt).hidden;
}

export function getVisibleOptions(options) {
  return (options || []).map(normalizeOption).filter((opt) => !opt.hidden);
}

export function normalizeField(field) {
  if (!field) return field;
  const type = field.type || 'text';

  if (type === 'section') {
    return {
      id: field.id || '',
      name: field.name || '',
      type: 'section',
      hidden: !!field.hidden,
      showWhen: normalizeShowWhen(field.showWhen),
      fields: Array.isArray(field.fields) ? field.fields.map(normalizeField) : [],
    };
  }

  return {
    id: field.id || '',
    name: field.name || '',
    type,
    value: field.value ?? '',
    required: field.required !== false,
    multiple: !!field.multiple,
    options: (field.options || []).map(normalizeOption),
    hint: (field.hint || '').toString(),
    hidden: !!field.hidden,
    showWhen: normalizeShowWhen(field.showWhen),
  };
}

export function getLeafFields(fields) {
  const result = [];
  (fields || []).forEach((rawField) => {
    const field = normalizeField(rawField);
    if (field.type === 'section') {
      result.push(...getLeafFields(field.fields));
    } else {
      result.push(field);
    }
  });
  return result;
}

export function getPriorFields(fields, targetId) {
  const prior = [];
  let found = false;

  const walk = (list) => {
    for (const rawField of list) {
      const field = normalizeField(rawField);
      if (field.id && field.id === targetId) {
        found = true;
        return;
      }
      if (field.type === 'section') {
        if (!found) {
          prior.push({
            id: field.id,
            name: field.name,
            type: 'section',
            options: [],
            multiple: false,
          });
        }
        walk(field.fields);
        if (found) return;
      } else if (!found) {
        prior.push({
          id: field.id,
          name: field.name,
          type: field.type,
          options: field.options,
          multiple: field.multiple,
        });
      }
    }
  };

  walk(fields || []);
  return prior;
}

export function evaluateCondition(showWhen, formValues) {
  if (!showWhen?.field) return true;

  const refValue = formValues[showWhen.field];
  const operator = showWhen.operator || 'equals';
  const target = showWhen.value;

  switch (operator) {
    case 'equals':
      if (Array.isArray(refValue)) return refValue.includes(target);
      return String(refValue ?? '') === String(target);
    case 'not_equals':
      if (Array.isArray(refValue)) return !refValue.includes(target);
      return String(refValue ?? '') !== String(target);
    case 'contains':
      if (Array.isArray(refValue)) {
        return refValue.some((value) => String(value).includes(String(target)));
      }
      return String(refValue ?? '').includes(String(target));
    case 'is_checked':
      return refValue === true;
    case 'is_filled':
      if (Array.isArray(refValue)) return refValue.length > 0;
      return refValue !== '' && refValue !== false && refValue != null;
    default:
      return true;
  }
}

export function isFieldVisible(field, formValues, parentVisible = true) {
  if (!parentVisible) return false;
  const normalized = normalizeField(field);
  if (normalized.hidden) return false;
  return evaluateCondition(normalized.showWhen, formValues);
}

/** Apply only `hidden` flags from editedFields onto baseFields (preserves structure). */
export function mergeFieldHiddenFlags(baseFields, editedFields) {
  const editedById = new Map();
  const editedByName = new Map();

  const indexEdited = (list) => {
    (list || []).forEach((raw) => {
      const field = normalizeField(raw);
      if (field.id) editedById.set(field.id, field);
      if (field.name) editedByName.set(`${field.type}:${field.name}`, field);
      if (field.type === 'section') indexEdited(field.fields);
    });
  };
  indexEdited(editedFields);

  const mergeOptions = (rawOptions, editedOptions) => {
    if (!Array.isArray(rawOptions)) return rawOptions;
    const editedByLabel = new Map(
      (editedOptions || []).map((opt) => [getOptionLabel(opt), normalizeOption(opt)])
    );
    return rawOptions.map((rawOpt) => {
      const label = getOptionLabel(rawOpt);
      const edited = editedByLabel.get(label);
      const hidden = edited ? !!edited.hidden : !!normalizeOption(rawOpt).hidden;
      if (typeof rawOpt === 'string') {
        return hidden ? { label: rawOpt, maxCount: null, hidden: true } : rawOpt;
      }
      return { ...rawOpt, hidden };
    });
  };

  const mergeList = (list) => (list || []).map((raw) => {
    const field = normalizeField(raw);
    const match = (field.id && editedById.get(field.id))
      || editedByName.get(`${field.type}:${field.name}`)
      || null;
    const hidden = match ? !!match.hidden : !!field.hidden;

    if ((raw?.type || field.type) === 'section') {
      return {
        ...raw,
        hidden,
        fields: mergeList(raw.fields || field.fields),
      };
    }

    if ((raw?.type || field.type) === 'select') {
      return {
        ...raw,
        hidden,
        options: mergeOptions(raw.options || field.options, match?.options),
      };
    }

    return { ...raw, hidden };
  });

  return mergeList(baseFields);
}

export function pruneHiddenFieldValues(formValues, fields, parentVisible = true) {
  const next = { ...formValues };

  (fields || []).forEach((rawField) => {
    const field = normalizeField(rawField);
    if (field.type === 'section') {
      const sectionVisible = isFieldVisible(field, next, parentVisible);
      Object.assign(next, pruneHiddenFieldValues(next, field.fields, sectionVisible));
      return;
    }

    if (!isFieldVisible(field, next, parentVisible)) {
      if (field.type === 'select' && field.multiple) next[field.name] = [];
      else if (field.type === 'checkbox') next[field.name] = false;
      else next[field.name] = '';
      return;
    }

    if (field.type === 'select') {
      const visibleLabels = new Set(getVisibleOptions(field.options).map((opt) => opt.label));
      if (field.multiple) {
        const current = Array.isArray(next[field.name]) ? next[field.name] : [];
        next[field.name] = current.filter((value) => visibleLabels.has(value));
      } else if (next[field.name] && !visibleLabels.has(next[field.name])) {
        next[field.name] = '';
      }
    }
  });

  return next;
}

export function validateVisibleFields(fields, formValues, parentVisible = true) {
  const invalid = [];

  (fields || []).forEach((rawField) => {
    const field = normalizeField(rawField);
    if (field.type === 'section') {
      const sectionVisible = isFieldVisible(field, formValues, parentVisible);
      invalid.push(...validateVisibleFields(field.fields, formValues, sectionVisible));
      return;
    }

    if (!isFieldVisible(field, formValues, parentVisible)) return;
    if (!validateFieldValue(field, formValues[field.name])) {
      invalid.push(field.name);
    }
  });

  return invalid;
}

export function getVisibleLeafFields(fields, formValues, parentVisible = true) {
  const result = [];

  (fields || []).forEach((rawField) => {
    const field = normalizeField(rawField);
    if (field.type === 'section') {
      const sectionVisible = isFieldVisible(field, formValues, parentVisible);
      result.push(...getVisibleLeafFields(field.fields, formValues, sectionVisible));
      return;
    }
    if (isFieldVisible(field, formValues, parentVisible)) {
      result.push(field);
    }
  });

  return result;
}

export function ensureFieldsHaveIds(fields) {
  return (fields || []).map((rawField) => {
    const field = normalizeField(rawField);
    if (field.type === 'section') {
      return {
        ...field,
        id: field.id || generateFieldId(),
        fields: ensureFieldsHaveIds(field.fields),
      };
    }
    return {
      ...field,
      id: field.id || generateFieldId(),
    };
  });
}

export function getConditionSummary(showWhen) {
  if (!showWhen?.field) return null;
  const operator = CONDITION_OPERATORS.find((op) => op.value === showWhen.operator);
  const operatorLabel = operator?.label || showWhen.operator;
  if (OPERATORS_WITHOUT_VALUE.includes(showWhen.operator)) {
    return `se "${showWhen.field}" ${operatorLabel}`;
  }
  return `se "${showWhen.field}" ${operatorLabel} "${showWhen.value}"`;
}

export function decodeImgSrc(maybeData) {
  if (!maybeData) return null;
  if (typeof maybeData === 'string' && maybeData.startsWith('<img')) {
    const match = maybeData.match(/src="([^"]+)"/);
    if (match) return match[1];
  }
  return maybeData;
}

export function stripHtml(val) {
  return (val || '').toString().replace(/<[^>]*>/g, '');
}

const DESCRIPTION_ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'a'];
const DESCRIPTION_ALLOWED_ATTR = ['href', 'target', 'rel', 'style'];
const DESCRIPTION_ALLOWED_TEXT_ALIGN = new Set(['left', 'center', 'right']);
const HTML_MARKUP_RE = /<\/?[a-z][\s\S]*>/i;

let descriptionPurifyHooked = false;

function normalizeAllowedTextAlign(value) {
  const align = (value || '').toString().trim().toLowerCase();
  return DESCRIPTION_ALLOWED_TEXT_ALIGN.has(align) ? align : '';
}

function ensureDescriptionPurifyHook() {
  if (descriptionPurifyHooked || typeof window === 'undefined') return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (href && !/^(https?:|mailto:)/i.test(href)) {
        node.removeAttribute('href');
      }
      if (node.hasAttribute('href')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      } else {
        node.removeAttribute('target');
        node.removeAttribute('rel');
      }
    }

    if (node.hasAttribute && node.hasAttribute('style')) {
      const align = normalizeAllowedTextAlign(
        node.style?.textAlign || /text-align\s*:\s*([a-z]+)/i.exec(node.getAttribute('style') || '')?.[1]
      );
      if (align) {
        node.setAttribute('style', `text-align: ${align}`);
      } else {
        node.removeAttribute('style');
      }
    }
  });
  descriptionPurifyHooked = true;
}

export function hasRichHtmlMarkup(val) {
  return HTML_MARKUP_RE.test((val || '').toString());
}

export function sanitizeDescriptionHtml(val) {
  const str = (val || '').toString();
  if (!str) return '';
  if (typeof window === 'undefined') return stripHtml(str);
  ensureDescriptionPurifyHook();
  return DOMPurify.sanitize(str, {
    ALLOWED_TAGS: DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: DESCRIPTION_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}

export function descriptionToPlainText(val) {
  const str = (val || '').toString();
  if (!str) return '';
  const withBreaks = str
    .replace(/<\/p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/(ul|ol)>/gi, '\n');
  return stripHtml(withBreaks)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function isEmptyRichText(val) {
  return !descriptionToPlainText(val);
}

export function normalizeDescriptionForSave(val) {
  if (isEmptyRichText(val)) return '';
  if (!hasRichHtmlMarkup(val)) return (val || '').toString().trim();
  return sanitizeDescriptionHtml(val);
}

export function normalizeInvitation(raw) {
  if (!raw) return null;
  const theme = { ...DEFAULT_THEME, ...(raw.theme || {}) };
  return {
    name: raw.name || '',
    description: raw.description || raw.text || '',
    logo: raw.logo || '',
    image: raw.image || '',
    color1: raw.color1 || '#F68B1F',
    color2: raw.color2 || '#395E14',
    fields: Array.isArray(raw.fields) ? raw.fields.map(normalizeField) : [],
    theme: {
      ...theme,
      backgroundImage: theme.backgroundImage || '',
      fontFamily: FONT_FAMILIES.some((f) => f.value === theme.fontFamily)
        ? theme.fontFamily
        : 'ubuntu',
    },
    location: normalizeLocation(raw.location),
    successMessage: (raw.successMessage || '').toString(),
    closedMessage: (raw.closedMessage || '').toString(),
    schemaVersion: raw.schemaVersion || 1,
    isOpen: raw.isOpen !== false,
    closesAt: raw.closesAt || null,
    eventDate: raw.eventDate || null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    responses: raw.responses,
  };
}

export function getInvitationBackgroundSrc(invitation) {
  return decodeImgSrc(normalizeInvitation(invitation)?.theme?.backgroundImage);
}

export function getInvitationCanvasStyle(invitation) {
  const bgSrc = getInvitationBackgroundSrc(invitation);
  return {
    ...INVITATION_LIGHT_THEME_VARS,
    colorScheme: 'light',
    ...(bgSrc ? { backgroundImage: `url(${bgSrc})` } : {}),
  };
}

export function isInvitationOpen(invitation) {
  const normalized = normalizeInvitation(invitation);
  if (!normalized) return false;
  if (normalized.isOpen === false) return false;
  if (normalized.closesAt) {
    const closeDate = parseLocalDateTime(normalized.closesAt) || new Date(normalized.closesAt);
    if (!Number.isNaN(closeDate.getTime()) && closeDate < new Date()) return false;
  }
  return true;
}

export function getContrastText(hexColor) {
  if (!hexColor) return '#000';
  let hex = hexColor.trim();
  if (hex.startsWith('#')) hex = hex.slice(1);
  if (hex.length === 3) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  if (!/^([0-9a-fA-F]{6})$/.test(hex)) return '#000';
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const srgbToLin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const R = srgbToLin(r);
  const G = srgbToLin(g);
  const B = srgbToLin(b);
  const L = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  const contrastWithWhite = 1.05 / (L + 0.05);
  const contrastWithBlack = (L + 0.05) / 0.05;
  return contrastWithBlack >= contrastWithWhite ? '#000' : '#fff';
}

export function slugifyInvitationName(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

export async function resolveUniqueSlug(baseName, excludeId = null) {
  const base = slugifyInvitationName(baseName);
  if (!base) return '';
  let candidate = base;
  let counter = 2;
  while (counter < 100) {
    try {
      const res = await axios.get(`${dbUrl}invitations/${candidate}.json`);
      if (!res.data) return candidate;
      if (excludeId && candidate === excludeId) return candidate;
    } catch {
      return candidate;
    }
    candidate = `${base}-${counter}`;
    counter += 1;
  }
  return candidate;
}

export function getInitialFormValues() {
  return {
    name: '',
    image: '',
    fields: [],
    color1: '#F68B1F',
    color2: '#395E14',
    logo: '',
    text: '',
    description: '',
    theme: { ...DEFAULT_THEME },
    location: { ...DEFAULT_LOCATION },
    successMessage: '',
    closedMessage: '',
    schemaVersion: 5,
    isOpen: true,
    closesAt: '',
    eventDate: '',
  };
}

export function formatEventDateLabel(eventDate) {
  return formatDateTimeDisplay(eventDate, { fallback: '' });
}

export function applyTemplate(templateId) {
  if (!templateId || templateId === 'none') {
    return getInitialFormValues();
  }
  const template = INVITATION_TEMPLATES.find((t) => t.id === templateId);
  if (!template) return getInitialFormValues();
  return {
    ...getInitialFormValues(),
    ...template.data,
    fields: template.data.fields.map((f) => normalizeField(f)),
  };
}

export function getInitialFieldValues(fields) {
  const initial = {};
  getLeafFields(fields).forEach((field) => {
    if (field.type === 'select' && field.multiple) initial[field.name] = [];
    else if (field.type === 'checkbox') initial[field.name] = false;
    else initial[field.name] = '';
  });
  return initial;
}

export function validateFieldValue(field, value) {
  const f = normalizeField(field);
  if (!f.required && (value === '' || value === false || (Array.isArray(value) && value.length === 0))) {
    return true;
  }
  if (f.type === 'email' && value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
  if (f.type === 'tel' && value) {
    return /^[+0-9\s()-]{6,}$/.test(value);
  }
  if (f.type === 'checkbox' && f.required) {
    return value === true;
  }
  if (f.required && (value === '' || value === false || (Array.isArray(value) && value.length === 0))) {
    return false;
  }
  return true;
}

export function toDatetimeLocalValue(isoOrEmpty) {
  return toDatetimeLocalValueFromLib(isoOrEmpty);
}

export async function updateInvitationClosure(invitationId, { isOpen, closesAt }) {
  await axios.patch(`${dbUrl}invitations/${invitationId}.json`, {
    isOpen,
    closesAt: closesAt || null,
    updatedAt: Date.now(),
  });
}
